import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import type { ReactNode } from "react";
import { AnnouncerProvider } from "~/components/a11y/announcer";
import { ToastProvider } from "~/components/ui/toast";
import { type AppEnvConfig, envConfig } from "~/config/env";
import { I18nProvider, type Locale } from "~/i18n";
import { ApiProvider } from "~/lib/api/api-context";
import { queryRetryDelay, shouldRetryQuery } from "~/lib/api/retry-policy";
import type { Transport } from "~/lib/api/transport";
import { AuthProvider } from "~/lib/auth/auth-context";

export function createQueryClient(): QueryClient {
  return new QueryClient({
    defaultOptions: {
      queries: {
        staleTime: 20_000,
        gcTime: 5 * 60_000,
        refetchOnWindowFocus: false,
        // Retrying a 403 just produces three identical refusals in the
        // audit log. Only genuinely transient failures are retried; 429s
        // honour Retry-After (see retry-policy).
        retry: shouldRetryQuery,
        retryDelay: queryRetryDelay,
      },
      mutations: { retry: false },
    },
  });
}

export function AppProviders({
  children,
  config = envConfig,
  queryClient,
  transport,
  locale,
}: {
  children: ReactNode;
  config?: AppEnvConfig;
  queryClient?: QueryClient;
  /** Test override; production picks the transport from `config.apiMode`. */
  transport?: Transport;
  locale?: Locale;
}) {
  const client = queryClient ?? createQueryClient();

  return (
    <I18nProvider initialLocale={locale}>
      <AnnouncerProvider>
        <ToastProvider>
          <AuthProvider config={config}>
            <ApiProvider config={config} transport={transport}>
              <QueryClientProvider client={client}>{children}</QueryClientProvider>
            </ApiProvider>
          </AuthProvider>
        </ToastProvider>
      </AnnouncerProvider>
    </I18nProvider>
  );
}
